import React, { useEffect, useRef, useState } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import useImage from 'use-image';
import { ContourShape, MeasureOverlay, ScaleRect } from './ScaleCanvasParts';
import { cleanFigure, distance } from './wizardUtils';

const MIN_SCALE = 0.1;
const MAX_SCALE = 5;
const SCALE_BY = 1.2;

// Zoomable/pannable canvas showing a scale figure and letting the user
// measure the length of the printed scale.
export default function ScaleCanvas({
  figure,
  image,
  onChangeFigure,
  isDrawing,
  onDrawingComplete
}) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [stageScale, setStageScale] = useState(1);
  const [origin, setOrigin] = useState({ x: 0, y: 0 });
  const [measure, setMeasure] = useState(null);
  const containerRef = useRef(null);

  const [imageNode, status] = useImage(image?.src || image?.href);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !image) return;

    setDimensions({
      width: container.offsetWidth,
      height: (container.offsetWidth / image.width) * image.height
    });
    setStageScale(container.offsetWidth / image.width);
  }, [image, status, containerRef.current?.offsetWidth]);

  function handleWheel(e) {
    e.evt.preventDefault();

    const stage = e.target.getStage();
    const oldScale = stage.scaleX();
    const pointer = stage.getPointerPosition();
    const focus = {
      x: pointer.x / oldScale - stage.x() / oldScale,
      y: pointer.y / oldScale - stage.y() / oldScale
    };

    const scale = Math.min(
      MAX_SCALE,
      Math.max(MIN_SCALE, e.evt.deltaY < 0 ? oldScale * SCALE_BY : oldScale / SCALE_BY)
    );
    setStageScale(scale);
    setOrigin({
      x: -(focus.x - pointer.x / scale) * scale,
      y: -(focus.y - pointer.y / scale) * scale
    });
  }

  // Pointer position in image (stage content) coordinates.
  function pointerPosition(e) {
    const stage = e.target?.getStage?.();
    const pointer = stage?.getPointerPosition?.();
    if (!stage || !pointer) return null;

    const scale = stage.scaleX();
    return { x: (pointer.x - stage.x()) / scale, y: (pointer.y - stage.y()) / scale };
  }

  const reportWidth = (width) =>
    onChangeFigure?.(figure.id, { ...cleanFigure(figure), manualWidth: width });

  function handleMouseDown(e) {
    if (!isDrawing) return;
    const point = pointerPosition(e);
    if (!point) return;

    e.evt?.preventDefault();
    setMeasure({ start: point, end: point });
  }

  function handleMouseMove(e) {
    if (!isDrawing || !measure) return;
    const point = pointerPosition(e);
    if (!point) return;

    e.evt?.preventDefault();
    setMeasure({ ...measure, end: point });
  }

  function handleMouseUp(e) {
    if (!isDrawing || !measure) return;
    e.evt?.preventDefault();

    setMeasure({ ...measure, done: true });
    reportWidth(distance(measure.start, measure.end));
    onDrawingComplete?.();
  }

  // Dragging the start point moves the whole measure line, dragging the end
  // resizes it and reports the measured width.
  function dragHandle(which, e) {
    e.evt?.preventDefault();
    const point = pointerPosition(e);
    if (!point || !measure) return;

    if (which === 'start') {
      setMeasure({
        ...measure,
        start: point,
        end: {
          x: measure.end.x + (point.x - measure.start.x),
          y: measure.end.y + (point.y - measure.start.y)
        }
      });
      return;
    }

    setMeasure({ ...measure, end: point });
    reportWidth(distance(measure.start, point));
  }

  if (status !== 'loaded' || !imageNode) {
    return <div className="alert alert-info">Loading image...</div>;
  }

  return (
    <div ref={containerRef} className="scale-canvas-container">
      <Stage
        onWheel={handleWheel}
        scaleX={stageScale}
        scaleY={stageScale}
        x={origin.x}
        y={origin.y}
        width={dimensions.width}
        height={dimensions.height}
        draggable={!isDrawing}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <Layer>
          <KonvaImage image={imageNode} width={image.width} height={image.height} x={0} y={0} />
          <ScaleRect figure={figure} />
          <ContourShape points={contourPoints(figure)} />
          {measure && (
            <MeasureOverlay measure={measure} scale={stageScale} onDragHandle={dragHandle} />
          )}
        </Layer>
      </Stage>
    </div>
  );
}

function contourPoints(figure) {
  if (!figure?.contour?.length) return null;
  return figure.contour.map(([x, y]) => [x + figure.x1, y + figure.y1]);
}
